import React, { useEffect } from "react"
import { useParams, Navigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, CardBody } from "reactstrap"
import { Link } from "react-router-dom"
import FeatherIcon from "feather-icons-react"

// Import from constants
import { SERVICE_CONFIG, SERVICE_KEYS } from "../../constants"

const ServiceDetailPage = () => {
    const { serviceKey } = useParams()
    const { t } = useTranslation()

    // Scroll to top on page load or service change
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [serviceKey])

    // Validate service key
    if (!SERVICE_CONFIG[serviceKey]) {
        return <Navigate to="/" />
    }

    const config = SERVICE_CONFIG[serviceKey]
    const features = t(`services.${serviceKey}.features`, { returnObjects: true }) || []

    return (
        <React.Fragment>
            {/* Hero Section */}
            <section
                className="bg-half-170 d-table w-100"
                style={{
                    background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)"
                }}
            >
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} md={6}>
                            <div className="title-heading">
                                <span
                                    className="badge rounded-pill mb-3"
                                    style={{ backgroundColor: config.color, padding: "8px 16px" }}
                                >
                                    <FeatherIcon icon={config.icon} style={{ width: "14px", height: "14px" }} className="me-1" />
                                    {t("common.enterpriseModule", "Kurumsal Modül")}
                                </span>
                                <h1 className="heading text-white mb-3">
                                    {t(`services.${serviceKey}.title`)}
                                </h1>
                                <p className="text-white-50 para-desc">
                                    {t(`services.${serviceKey}.description`)}
                                </p>
                                <div className="mt-4 pt-2">
                                    <Link to="/contact-us" className="btn btn-primary me-2">
                                        Demo Talep Et
                                        <FeatherIcon icon="arrow-right" className="fea icon-sm ms-1" />
                                    </Link>
                                    <a href="/#services" className="btn btn-light">
                                        Tüm Hizmetler
                                    </a>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6} md={6} className="mt-4 pt-2 mt-sm-0 pt-sm-0">
                            {/* Feature Highlights */}
                            <Row className="g-3">
                                {Array.isArray(features) && features.slice(0, 6).map((feature, idx) => (
                                    <Col xs={6} key={idx}>
                                        <div
                                            className="p-3 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                                backdropFilter: "blur(10px)",
                                                border: "1px solid rgba(255,255,255,0.1)"
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                    style={{
                                                        width: "28px",
                                                        height: "28px",
                                                        backgroundColor: config.color,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <FeatherIcon
                                                        icon="check"
                                                        style={{ width: "14px", height: "14px", color: "#fff" }}
                                                    />
                                                </div>
                                                <small className="text-white fw-medium" style={{ fontSize: "12px" }}>{feature}</small>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Main Content Section */}
            <section className="section">
                <Container>
                    <Row>
                        {/* Main Content */}
                        <Col lg={8}>
                            {/* Module Specific Title */}
                            <div className="mb-5">
                                <h3 className="mb-3">
                                    {t("common.featuresTitle", "Özellikler")}
                                </h3>
                                <p className="text-muted">
                                    {t(`services.${serviceKey}.description`)}
                                </p>
                            </div>

                            {/* Features as Icon Pills Grid */}
                            <Row className="g-4 mb-5">
                                {Array.isArray(features) && features.map((feature, idx) => (
                                    <Col md={6} key={idx}>
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="rounded d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    minWidth: "40px",
                                                    backgroundColor: `${config.color}15`
                                                }}
                                            >
                                                <FeatherIcon
                                                    icon="check"
                                                    style={{ width: "20px", height: "20px", color: config.color }}
                                                />
                                            </div>
                                            <span style={{ fontWeight: 500 }}>{feature}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Benefits Section */}
                            <div
                                className="p-4 rounded-3 mb-4"
                                style={{ backgroundColor: "#f8f9fa" }}
                            >
                                <h4 className="mb-4">{t("common.advantages")}</h4>
                                <Row>
                                    <Col md={4} className="text-center mb-4 mb-md-0">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: "60px", height: "60px", backgroundColor: "rgba(230, 81, 0, 0.1)" }}
                                        >
                                            <FeatherIcon icon="clock" style={{ width: "28px", height: "28px", color: "#E65100" }} />
                                        </div>
                                        <h6>{t("common.timeSaving")}</h6>
                                        <small className="text-muted">{t("common.timeSavingDesc")}</small>
                                    </Col>
                                    <Col md={4} className="text-center mb-4 mb-md-0">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: "60px", height: "60px", backgroundColor: "rgba(46, 125, 50, 0.1)" }}
                                        >
                                            <FeatherIcon icon="trending-up" style={{ width: "28px", height: "28px", color: "#2E7D32" }} />
                                        </div>
                                        <h6>{t("common.efficiency")}</h6>
                                        <small className="text-muted">{t("common.efficiencyDesc")}</small>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                            style={{ width: "60px", height: "60px", backgroundColor: "rgba(21, 101, 192, 0.1)" }}
                                        >
                                            <FeatherIcon icon="shield" style={{ width: "28px", height: "28px", color: "#1565C0" }} />
                                        </div>
                                        <h6>{t("common.security")}</h6>
                                        <small className="text-muted">{t("common.securityDesc")}</small>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        {/* Sidebar */}
                        <Col lg={4} className="mt-4 mt-lg-0">
                            <div className="sticky-bar" style={{ top: "100px" }}>
                                {/* Other Services */}
                                <Card className="border-0 shadow rounded mb-4">
                                    <CardBody className="p-4">
                                        <h5 className="mb-4">{t("common.otherServices")}</h5>
                                        <div className="d-flex flex-column gap-2">
                                            {SERVICE_KEYS.map((key, idx) => {
                                                const sConfig = SERVICE_CONFIG[key]
                                                const isActive = serviceKey === key
                                                return (
                                                    <Link
                                                        key={idx}
                                                        to={`/services/${key}`}
                                                        className={`d-flex align-items-center p-2 rounded text-decoration-none ${isActive ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                                                        style={{ transition: "all 0.2s" }}
                                                    >
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                backgroundColor: isActive ? "rgba(255,255,255,0.2)" : `${sConfig.color}15`,
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <FeatherIcon
                                                                icon={sConfig.icon}
                                                                style={{
                                                                    width: "16px",
                                                                    height: "16px",
                                                                    color: isActive ? "#fff" : sConfig.color
                                                                }}
                                                            />
                                                        </div>
                                                        <small className="fw-medium">{t(`services.${key}.title`)}</small>
                                                        {isActive && (
                                                            <FeatherIcon icon="chevron-right" className="ms-auto" style={{ width: "16px", height: "16px" }} />
                                                        )}
                                                    </Link>
                                                )
                                            })}
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* CTA Card */}
                                <Card
                                    className="border-0 shadow rounded"
                                    style={{ background: "linear-gradient(135deg, #E65100 0%, #BF360C 100%)" }}
                                >
                                    <CardBody className="p-4 text-center text-white">
                                        <FeatherIcon icon="phone" style={{ width: "36px", height: "36px" }} className="mb-3" />
                                        <h5 className="text-white mb-2">{t("common.haveQuestions")}</h5>
                                        <p className="text-white-50 small mb-3">{t("common.expertTeam")}</p>
                                        <Link to="/contact-us" className="btn btn-light btn-sm">
                                            {t("common.contactUs")}
                                        </Link>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section
                className="section bg-primary"
                style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)" }}
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} className="text-center">
                            <h4 className="text-white mb-3">
                                {t(`services.${serviceKey}.title`)} {t("common.readyToMeet")}
                            </h4>
                            <p className="text-white-50 mb-4">
                                {t("common.discoverPlatform")}
                            </p>
                            <Link to="/contact-us" className="btn btn-primary">
                                <FeatherIcon icon="calendar" className="fea icon-sm me-2" />
                                {t("common.scheduleDemo")}
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    )
}

export default ServiceDetailPage
